import type { ComboboxItem, ComboboxItemGroup } from '@mantine/core';
import { QueryObserverResult, useMutation, useQueries, useQuery, UseQueryOptions } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { getBoardsByTeam } from '../../../api/projects-client/projects-client';
import type { ProjectsResponse } from '../../../api/projects-client/projects-client.type';
import {
  createWorkPeriodGoal,
  deleteWorkPeriodGoal,
  getWorkPeriods,
  getWorkPeriodsMeasure,
  getWorkPeriodTaskTypeBreakdown,
  updateWorkPeriodGoal,
} from '../../../api/work-periods-client/work-periods-client';
import {
  useAvailableMeasures,
  useWorkPeriodsMeasureSingular,
} from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  type HealthScoreData,
  Measure,
  type MeasureMetadata,
  ModuleType,
  type TaskTypeBreakdownResponse,
  Transformer,
  type UseWorkPeriodsMeasurePayloadSingular,
  type WorkPeriodGoal,
} from '../../../api/work-periods-client/work-periods-client.type';
import { getLastNotNullValue } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import { useDeepCompareEffect } from '../../../hooks/useDeepCompareEffect';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import type { FlowData, KeyProcessMeasuresData, ReadinessData, WorkPeriod } from '../work-periods.type';
import { WorkPeriodType } from '../work-periods.type';

/**
 * Custom hook to fetch work periods for a given board
 *
 * @param {string | null} boardId - The ID of the board
 * @returns {object} - An object containing the fetched data and an isLoading flag
 */
const useWorkPeriodsData = (boardId: string | null) => {
  const query = useQuery({
    queryKey: ['work-periods', boardId],
    queryFn: () => getWorkPeriods({ subproject_id: boardId || '' }),
    enabled: !!boardId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return { data: query.data, isLoading: query.isLoading, isFetching: query.isFetching };
};

/**
 * Custom hook to create the payload for fetching measures data
 *
 * @param {Measure[]} measures - An array of measures to be fetched
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @param {{ startDate: Date | null; endDate: Date | null }} dateRange - The date range for ad-hoc work periods
 * @param {WorkPeriodType} workPeriodType - The type of work period (predefined or ad-hoc)
 * @returns {UseWorkPeriodsMeasurePayloadSingular} - The payload for fetching measures data
 */
const usePayload = (
  measures: Measure[],
  boardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  dateRange: { startDate: Date | null; endDate: Date | null },
  workPeriodType: WorkPeriodType,
): UseWorkPeriodsMeasurePayloadSingular => {
  const defaultPayload = {
    subproject_id: boardId || '',
    measure_name: measures,
    module_type: ModuleType.WorkPeriod,
  };

  const customPayload =
    workPeriodType === WorkPeriodType.Defined && selectedWorkPeriods.length > 0
      ? {
          work_period_id: selectedWorkPeriods[0].id,
          start_date: dayjs(selectedWorkPeriods[0].start_date).format('YYYY-MM-DD'),
          end_date: dayjs(selectedWorkPeriods[0].end_date).format('YYYY-MM-DD'),
        }
      : {
          start_date: dateRange.startDate ? dayjs(dateRange.startDate).format('YYYY-MM-DD') : '',
          end_date: dateRange.endDate ? dayjs(dateRange.endDate).format('YYYY-MM-DD') : '',
        };

  return {
    ...defaultPayload,
    ...customPayload,
  } as UseWorkPeriodsMeasurePayloadSingular;
};

/**
 * Custom hook to fetch the health score data
 *
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @param {{ startDate: Date | null; endDate: Date | null }} dateRange - The date range for ad-hoc work periods
 * @param {WorkPeriodType} workPeriodType - The type of work period (predefined or ad-hoc)
 * @returns {object} - An object containing the fetched data and an isLoading flag
 */
const useHealthScoreData = (
  boardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  dateRange: { startDate: Date | null; endDate: Date | null },
  workPeriodType: WorkPeriodType,
) => {
  // Ensure selectedWorkPeriods is an array
  const safeSelectedWorkPeriods = Array.isArray(selectedWorkPeriods) ? selectedWorkPeriods : [];

  const payload = usePayload([Measure.HealthScores], boardId, safeSelectedWorkPeriods, dateRange, workPeriodType);
  const { data, query } = useWorkPeriodsMeasureSingular<HealthScoreData>({
    payload: {
      ...payload,
      transformer: Transformer.TimeSeriesMostRecentNotNull,
    },
    options: {
      enabled:
        !!boardId &&
        ((workPeriodType === WorkPeriodType.Defined && safeSelectedWorkPeriods.length > 0) ||
          (workPeriodType === WorkPeriodType.Custom && !!dateRange.startDate && !!dateRange.endDate)),
    },
  });

  // Ensure we always return a properly structured object even if data is missing
  const safeData: HealthScoreData = {
    health_scores: {
      score_by_points: data?.health_scores?.score_by_points ?? 0,
      score_by_tasks: data?.health_scores?.score_by_tasks ?? 0,
      ...(data?.health_scores || {}),
    },
    ...(data || {}),
  };

  return { data: safeData, isFetching: query.isFetching };
};

/**
 * Custom hook to fetch the key process measures data
 *
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @param {{ startDate: Date | null; endDate: Date | null }} dateRange - The date range for ad-hoc work periods
 * @param {WorkPeriodType} workPeriodType - The type of work period (predefined or ad-hoc)
 * @returns {object} - An object containing the fetched data and an isLoading flag
 */
const useKeyProcessMeasuresData = (
  boardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  dateRange: { startDate: Date | null; endDate: Date | null },
  workPeriodType: WorkPeriodType,
) => {
  const enabled =
    !!boardId &&
    ((workPeriodType === WorkPeriodType.Defined && selectedWorkPeriods.length > 0) ||
      (workPeriodType === WorkPeriodType.Custom && !!dateRange.startDate && !!dateRange.endDate));

  const options = { enabled };

  // Strategy measure
  const strategyPayload = usePayload([Measure.Strategy], boardId, selectedWorkPeriods, dateRange, workPeriodType);
  const { data: strategyData, query: strategyQuery } = useWorkPeriodsMeasureSingular<Partial<KeyProcessMeasuresData>>({
    payload: {
      ...strategyPayload,
      transformer: Transformer.LastValueIndexed,
    },
    options,
  });

  // Complexity measure
  const complexityPayload = usePayload([Measure.Complexity], boardId, selectedWorkPeriods, dateRange, workPeriodType);
  const { data: complexityData, query: complexityQuery } = useWorkPeriodsMeasureSingular<
    Partial<KeyProcessMeasuresData>
  >({
    payload: {
      ...complexityPayload,
      transformer: Transformer.SingleLastNonNull,
      is_custom: true,
    },
    options,
  });

  // Quality measure
  const qualityPayload = usePayload([Measure.Quality], boardId, selectedWorkPeriods, dateRange, workPeriodType);
  const { data: qualityData, query: qualityQuery } = useWorkPeriodsMeasureSingular<Partial<KeyProcessMeasuresData>>({
    payload: {
      ...qualityPayload,
      transformer: Transformer.TimeSeriesMostRecentNotNull,
    },
    options,
  });

  // Scope measure
  const scopePayload = usePayload([Measure.ScopeCreep], boardId, selectedWorkPeriods, dateRange, workPeriodType);
  const { data: scopeData, query: scopeQuery } = useWorkPeriodsMeasureSingular<Partial<KeyProcessMeasuresData>>({
    payload: {
      ...scopePayload,
      transformer: Transformer.CumulativeSingularValue,
    },
    options,
  });

  // Readiness measure
  // USES CUSTOM MEASURE ENDPOINT - DIFFERENT FROM OTHER MEASURES
  const readinessPayload = usePayload([Measure.Readiness], boardId, selectedWorkPeriods, dateRange, workPeriodType);
  const { data: rawReadinessData, query: readinessQuery } = useWorkPeriodsMeasureSingular<ReadinessData>({
    payload: {
      ...readinessPayload,
      is_custom: true,
      module_type: ModuleType.WorkPeriod,
      transformer: Transformer.ReturnFirstMetricOnly,
    },
    options,
  });
  const lastReadinessValue = getLastNotNullValue(rawReadinessData?.readiness);
  const readinessData = {
    readiness: lastReadinessValue as Partial<KeyProcessMeasuresData>,
  };

  // Independence measure
  const independencePayload = usePayload(
    [Measure.Independence],
    boardId,
    selectedWorkPeriods,
    dateRange,
    workPeriodType,
  );
  const { data: independenceData, query: independenceQuery } = useWorkPeriodsMeasureSingular<
    Partial<KeyProcessMeasuresData>
  >({
    payload: {
      ...independencePayload,
      transformer: Transformer.LastValueIndexed,
    },
    options,
  });

  const isFetching =
    strategyQuery.isFetching ||
    complexityQuery.isFetching ||
    qualityQuery.isFetching ||
    scopeQuery.isFetching ||
    readinessQuery.isFetching ||
    independenceQuery.isFetching;

  const data = {
    ...strategyData,
    ...complexityData,
    ...qualityData,
    ...scopeData,
    ...readinessData,
    ...independenceData,
  } as KeyProcessMeasuresData;

  return { data, isFetching };
};

/**
 * Custom hook to fetch the flow data
 *
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @param {{ startDate: Date | null; endDate: Date | null }} dateRange - The date range for ad-hoc work periods
 * @param {WorkPeriodType} workPeriodType - The type of work period (predefined or ad-hoc)
 * @returns {object} - An object containing the fetched data and an isLoading flag
 */
const useFlowData = (
  boardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  dateRange: { startDate: Date | null; endDate: Date | null },
  workPeriodType: WorkPeriodType,
) => {
  // Ensure selectedWorkPeriods is an array
  const safeSelectedWorkPeriods = Array.isArray(selectedWorkPeriods) ? selectedWorkPeriods : [];

  const enabled =
    !!boardId &&
    ((workPeriodType === WorkPeriodType.Defined && safeSelectedWorkPeriods.length > 0) ||
      (workPeriodType === WorkPeriodType.Custom && !!dateRange.startDate && !!dateRange.endDate));

  // Flow by phase
  const flowByPhasePayload = usePayload(
    [Measure.FlowByPhase],
    boardId,
    safeSelectedWorkPeriods,
    dateRange,
    workPeriodType,
  );
  const { data: flowByPhaseData, query: flowByPhaseQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...flowByPhasePayload,
    },
    options: {
      enabled,
    },
  });

  // Flow by pace
  const flowByPacePayload = usePayload(
    [Measure.FlowByPace],
    boardId,
    safeSelectedWorkPeriods,
    dateRange,
    workPeriodType,
  );
  const { data: flowByPaceData, query: flowByPaceQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...flowByPacePayload,
    },
    options: {
      enabled,
    },
  });

  // Burns
  const burnsPayload = usePayload([Measure.Burns], boardId, safeSelectedWorkPeriods, dateRange, workPeriodType);
  const { data: burnsData, query: burnsQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...burnsPayload,
    },
    options: {
      enabled,
    },
  });

  // Historical burns
  const historicalBurnsPayload = usePayload(
    [Measure.HistoricalBurns],
    boardId,
    safeSelectedWorkPeriods,
    dateRange,
    workPeriodType,
  );
  const { data: historicalBurnsData, query: historicalBurnsQuery } = useWorkPeriodsMeasureSingular<FlowData>({
    payload: {
      ...historicalBurnsPayload,
    },
    options: {
      enabled,
    },
  });

  // Merge all data with defaults to ensure we have a complete structure
  const data = {
    ...(flowByPhaseData || {}),
    ...(flowByPaceData || {}),
    ...(burnsData || {}),
    ...(historicalBurnsData || {}),
  };

  const isFetching =
    flowByPhaseQuery.isFetching ||
    flowByPaceQuery.isFetching ||
    burnsQuery.isFetching ||
    historicalBurnsQuery.isFetching;

  return { data, isFetching };
};

/**
 * Custom hook to fetch available custom measures
 *
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @returns {object} - An object containing the fetched data and an isLoading flag
 */
const useCustomMeasures = (boardId: string | null, selectedWorkPeriods: WorkPeriod[]) => {
  const workPeriodId = selectedWorkPeriods.length > 0 ? selectedWorkPeriods[0].id : '';

  const { data: availableMeasuresResponse, query } = useAvailableMeasures({
    payload: {
      scope: [ModuleType.WorkPeriod],
      work_period_id: workPeriodId,
    },
    options: {
      enabled: !!boardId && !!workPeriodId,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
  });

  const availableMeasures = availableMeasuresResponse?.work_period?.measures || [];
  const customMeasures = availableMeasures.filter((measure: MeasureMetadata) => measure.is_custom);

  return { data: customMeasures, isLoading: query.isLoading, isFetching: query.isFetching };
};

/**
 * Custom hook to fetch measure data for multiple work periods
 *
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @param {MeasureMetadata[]} customMeasures - The custom measures to fetch data for
 * @returns {object} - An object containing the fetched data and an isLoading flag
 */
const useMeasureData = (
  boardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  customMeasures: MeasureMetadata[],
) => {
  // Get superuser mode from global store
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const organizationId = useGlobalStore((state) => state.organization?.id);

  // Ensure inputs are valid arrays
  const safeWorkPeriods = Array.isArray(selectedWorkPeriods) ? selectedWorkPeriods : [];
  const safeMeasures = Array.isArray(customMeasures) ? customMeasures : [];

  // Extract measure names
  const measureNames = safeMeasures.map((measure) => measure.measure_name);

  // Use useQueries instead of mapping over useQuery
  const workPeriodQueries = useQueries({
    queries: safeWorkPeriods.map((workPeriod) => {
      const workPeriodId = workPeriod?.id;
      const startDate = workPeriod?.start_date ? dayjs(workPeriod.start_date).format('YYYY-MM-DD') : undefined;
      const endDate = workPeriod?.end_date ? dayjs(workPeriod.end_date).format('YYYY-MM-DD') : undefined;

      // Determine if query should be enabled
      const isQueryEnabled = !!boardId && !!workPeriodId && Array.isArray(measureNames) && measureNames.length > 0;

      return {
        queryKey: ['measure-data', workPeriodId, measureNames, organizationId],
        queryFn: async () => {
          // Prepare payload for the API call
          const payload = {
            subproject_id: boardId || '',
            work_period_id: workPeriodId,
            start_date: startDate,
            end_date: endDate,
            measure_name: measureNames,
            is_custom: true,
            transformer: Transformer.ReturnFirstMetricOnly,
            module_type: ModuleType.WorkPeriod,
            organization_id: organizationId || undefined,
          };

          // Use the getWorkPeriodsMeasure function to fetch data
          return await getWorkPeriodsMeasure({
            ...payload,
            superuser_force_recalculation: superuserMode.enabled ? superuserMode.forceRecalculation : false,
          });
        },
        enabled: isQueryEnabled && !!organizationId,
      };
    }),
  });

  // Check if any queries are still fetching
  const isFetching = workPeriodQueries.some((query) => query.isFetching);

  // Combine results from all queries into a single object
  const measureData = workPeriodQueries.reduce(
    (acc, query, index) => {
      const workPeriodId = safeWorkPeriods[index]?.id;
      if (query.data && workPeriodId) {
        acc[workPeriodId] = query.data;
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  return { data: measureData, isFetching };
};

/**
 * Custom hook to fetch all data for the work periods view
 *
 * @param {string | null} boardId - The ID of the board
 * @param {WorkPeriod[]} selectedWorkPeriods - The selected work periods
 * @param {{ startDate: Date | null; endDate: Date | null }} dateRange - The date range for ad-hoc work periods
 * @param {WorkPeriodType} workPeriodType - The type of work period (predefined or ad-hoc)
 * @returns {object} - An object containing all the fetched data and an isLoading flag
 */
const useWorkPeriodsViewData = (
  boardId: string | null,
  selectedWorkPeriods: WorkPeriod[],
  dateRange: { startDate: Date | null; endDate: Date | null },
  workPeriodType: WorkPeriodType,
) => {
  // Ensure selectedWorkPeriods is always an array
  const safeSelectedWorkPeriods = Array.isArray(selectedWorkPeriods) ? selectedWorkPeriods : [];

  // Health score data
  const { data: scoreData, isFetching: isFetchingScoreData } = useHealthScoreData(
    boardId,
    safeSelectedWorkPeriods,
    dateRange,
    workPeriodType,
  );

  // Key process measures data
  const { data: keyMeasuresData, isFetching: isFetchingKeyMeasuresData } = useKeyProcessMeasuresData(
    boardId,
    safeSelectedWorkPeriods,
    dateRange,
    workPeriodType,
  );

  // Flow data
  const { data: flowData, isFetching: isFetchingFlowData } = useFlowData(
    boardId,
    safeSelectedWorkPeriods,
    dateRange,
    workPeriodType,
  );

  // Custom measures
  const { data: customMeasures, isFetching: isFetchingCustomMeasures } = useCustomMeasures(
    boardId,
    safeSelectedWorkPeriods,
  );

  // Measure data
  const { data: measureData, isFetching: isFetchingMeasureData } = useMeasureData(
    boardId,
    safeSelectedWorkPeriods,
    customMeasures || [],
  );

  const isLoading =
    isFetchingScoreData ||
    isFetchingKeyMeasuresData ||
    isFetchingFlowData ||
    isFetchingCustomMeasures ||
    isFetchingMeasureData;

  return {
    scoreData,
    keyMeasuresData,
    flowData,
    customMeasures,
    measureData,
    isLoading,
  };
};

/**
 * Custom hook to fetch boards for a team
 *
 * @param {string | null} teamId - The ID of the team
 * @returns {object} - An object containing the boards data and an isLoading flag
 */
const useBoards = (teamId: string | null) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['boards', teamId],
    queryFn: () => (teamId && teamId !== '1' ? getBoardsByTeam(teamId) : Promise.resolve([])),
    enabled: !!teamId && teamId !== '1',
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    boards: data || [],
    isLoading,
    error,
  };
};

/**
 * Generates an array of ComboboxItemGroup objects representing the board options.
 *
 * @param {ProjectsResponse[]} teams - The array of Team objects to generate the ComboboxItemGroup objects from.
 * @return {ComboboxItemGroup[]} An array of ComboboxItemGroup objects representing the board options.
 */
const getBoardOptions = (teams: ProjectsResponse[]): ComboboxItemGroup<ComboboxItem>[] => {
  return teams.map((team, index) => ({
    group: `${index + 1}-${team.name}`,
    items: team.subprojects
      .filter((subproject) => subproject.id && subproject.name)
      .map((subproject) => ({
        value: subproject.id,
        label: subproject.name,
      })),
  }));
};

/**
 * Extracts board IDs from the boards data
 *
 * @param {any[]} boards - Array of board data
 * @returns {string[]} - Array of board IDs
 */
const getBoardIds = (boards: any[]): string[] => {
  return boards.map((board) => board.id);
};

/**
 * Hook to access teams data from the global store
 * @returns {ProjectsResponse[]} Teams data
 */
const useTeams = (): ProjectsResponse[] => {
  return useGlobalStore((state) => state.teams);
};

/**
 * Manages board selection and synchronization with teams data.
 * Monitors teams changes, validates selected board ID, and sets default board if needed.
 *
 * @param {string | null} selectedBoardId - Currently selected board ID
 * @param {(boardId: string | null) => void} setSelectedBoardId - Function to update selected board ID
 * @returns {void}
 */
const useBoardManagement = (
  selectedBoardId: string | null,
  setSelectedBoardId: (boardId: string | null) => void,
  autoSelectDefault = true,
): void => {
  const teams = useTeams();

  useDeepCompareEffect(() => {
    if (!teams.length) {
      setSelectedBoardId(null);
      return;
    }

    const boardIds = getBoardIds(teams);
    const isValidBoard = selectedBoardId && boardIds.includes(selectedBoardId);

    if ((!isValidBoard || !selectedBoardId) && autoSelectDefault) {
      const defaultBoardId = boardIds[0] || null;
      setSelectedBoardId(defaultBoardId);
    }
  }, [teams]);
};

/**
 * Custom hook to update a work period goal
 *
 * @param {Function} onSuccess - A function to call when the mutation is successful
 * @return {UseMutationResult<WorkPeriodGoal, unknown, WorkPeriodGoal>} A mutation hook for updating a work period goal
 */
function useUpdateWorkPeriodGoalMutation(onSuccess?: (data: WorkPeriodGoal, variables: WorkPeriodGoal) => void) {
  return useMutation<WorkPeriodGoal, unknown, WorkPeriodGoal>({
    mutationFn: async (goal: WorkPeriodGoal) => {
      return await updateWorkPeriodGoal(goal);
    },
    onSuccess,
  });
}

/**
 * Hook for fetching task type breakdown data for a single work period
 *
 * @param {Object} params The hook parameters
 * @param {string} params.workPeriodId The ID of the work period
 * @param {Partial<UseQueryOptions<TaskTypeBreakdownResponse>>} [params.options] Optional react-query options
 * @returns {Object} Object containing the breakdown data and query result
 */
const useWorkPeriodTaskTypeBreakdown = ({
  workPeriodId,
  options,
}: {
  workPeriodId: string;
  options?: Partial<UseQueryOptions<TaskTypeBreakdownResponse>>;
}): {
  data: TaskTypeBreakdownResponse | undefined;
  query: QueryObserverResult<TaskTypeBreakdownResponse>;
} => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: ['work-period-task-type-breakdown', workPeriodId, organizationId] as const,
    queryFn: ({ signal }) => getWorkPeriodTaskTypeBreakdown(workPeriodId, organizationId || undefined, signal),
    ...options,
    enabled: (options?.enabled ?? true) && !!organizationId,
  });

  return { data: query.data, query };
};

/**
 * Custom hook to create a work period goal
 *
 * @param {Function} onSuccess - A function to call when the mutation is successful
 * @return {UseMutationResult<WorkPeriodGoal, unknown, Omit<WorkPeriodGoal, 'id'>>} A mutation hook for creating a work period goal
 */
function useCreateWorkPeriodGoalMutation(
  onSuccess?: (data: Omit<WorkPeriodGoal, 'id'>, variables: Omit<WorkPeriodGoal, 'id'>) => void,
) {
  return useMutation<WorkPeriodGoal, unknown, Omit<WorkPeriodGoal, 'id'>>({
    mutationFn: async (goal: Omit<WorkPeriodGoal, 'id'>) => {
      return await createWorkPeriodGoal(goal);
    },
    onSuccess,
  });
}

/**
 * Custom hook to delete a work period goal
 *
 * @param {Function} onSuccess - A function to call when the mutation is successful
 * @return {UseMutationResult<WorkPeriodGoal, unknown, WorkPeriodGoal>} A mutation hook for deleting a work period goal
 */
function useDeleteWorkPeriodGoalMutation(onSuccess?: (data: WorkPeriodGoal, variables: WorkPeriodGoal) => void) {
  return useMutation<WorkPeriodGoal, unknown, WorkPeriodGoal>({
    mutationFn: async (goal: WorkPeriodGoal) => {
      return await deleteWorkPeriodGoal(goal);
    },
    onSuccess,
  });
}

/**
 * Determines whether a work period selection exists (either defined periods or a custom date range).
 *
 * @returns {boolean} True if work periods are selected or a valid custom date range is set
 */
function useHasSelection(): boolean {
  const { selectedWorkPeriods, dateRange, selectedWorkPeriodType } = useWorkPeriodStore();
  return (
    (selectedWorkPeriodType === WorkPeriodType.Defined &&
      Array.isArray(selectedWorkPeriods) &&
      selectedWorkPeriods.length > 0) ||
    (selectedWorkPeriodType === WorkPeriodType.Custom && !!dateRange?.startDate && !!dateRange?.endDate)
  );
}

export {
  getBoardOptions,
  useHasSelection,
  useBoardManagement,
  useBoards,
  useCreateWorkPeriodGoalMutation,
  useDeleteWorkPeriodGoalMutation,
  useTeams,
  useUpdateWorkPeriodGoalMutation,
  useWorkPeriodsData,
  useWorkPeriodsViewData,
  useWorkPeriodTaskTypeBreakdown,
};
